import { useState, useCallback, useMemo } from "react";
import { z } from "zod";
import { useParams, useSearchParams } from "react-router";
import { applicationLogger } from "@frontend/shared/infrastructure/logger/application-logger.singleton";
import {
  extractApiErrorCode,
  extractApiErrorData,
  normalizeError,
} from "@frontend/shared/infrastructure/errors/error.utils";
import { LocalizedTextUtils } from "@shared/utils/localized-text.utils";
import { useUserActions } from "@frontend/shared/features/actions/application/actions/use-user-actions";
import { useRequiredEventRouteContext } from "@/shared/presentation/hooks/useEventRouteContext";
import { DirectoryConnectionStatus } from "@shared/schemas/profile-detail.schemas";
import {
  ConnectionRequestIdPublic,
  EventIdPublic,
  EventIdPublicOrSlug,
  EventParticipantIdPublic,
} from "@shared/schemas/branded-id.schemas";
import { FeatureKey } from "@shared/schemas/feature-key.schemas";
import { IntroRequestStatus } from "@shared/schemas/intro-request-state.schemas";
import { SuggestionType } from "@shared/schemas/suggestion-type.schemas";
import { UserIdentity } from "@shared/schemas/user-identity.schemas";
import type { ThemeModeType } from "@shared/types/theme-mode.types";
import type { ProfileContactInfo } from "@shared/schemas/profile-detail.schemas";
import type { DirectoryProfile } from "@frontend/shared/domain/types/directory-profile.types";
import type { EventPublicView } from "@shared/schemas/event.schemas";
import { i18n } from "@frontend/i18n";
import { type RespondToRequestActionType } from "../../domain/types/repository-options.types";
import { useFeatureConfigs } from "@frontend/shared/features/feature-config/application/hooks/use-feature-configs";
import { DateUtils } from "@frontend/shared/presentation/utils/date.utils";
import type { Suggestion } from "@shared/schemas/suggestion.schemas";

const IntroRateLimitPayloadSchema = z.object({
  limit: z.number(),
  retryAfterMinutes: z.number(),
});

type ToastType = "success" | "error" | "info";

function showToast(
  title: string,
  description?: string,
  type: ToastType = "info",
): void {
  const prefix = `[TOAST:${type.toUpperCase()}]`;
  if (description !== undefined) {
    applicationLogger.debug(`${prefix} ${title} - ${description}`);
  } else {
    applicationLogger.debug(`${prefix} ${title}`);
  }
}

function createEmailHandler(
  contactInfo: ProfileContactInfo | null,
  participantIdPublic: string,
): () => Promise<boolean> {
  return async (): Promise<boolean> => {
    if (contactInfo?.email === null || contactInfo?.email === undefined) {
      applicationLogger.warn(
        "[Profile] Email contact action triggered but no email available",
        "useProfileDetailController.handleEmailContact",
        [{ participantIdPublic }],
      );
      showToast(
        i18n.t("user.directory.toast.noEmailAvailable"),
        i18n.t("user.directory.toast.noEmailAvailableDescription"),
        "error",
      );
      return false;
    }

    if (
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Runtime guard: clipboard API may be unavailable in insecure contexts (HTTP)
      navigator.clipboard === undefined ||
      typeof navigator.clipboard.writeText !== "function"
    ) {
      applicationLogger.error(
        "Clipboard API (writeText) is not available",
        "useProfileDetailController.handleEmailContact",
        new Error(
          !window.isSecureContext
            ? "Insecure context (not HTTPS)"
            : "Clipboard API unsupported",
        ),
      );
      showToast(
        i18n.t("user.directory.toast.emailCopyFailed"),
        i18n.t("user.directory.toast.emailCopyFailedDescription"),
        "error",
      );
      return false;
    }

    const email = String(contactInfo.email);
    applicationLogger.debug("[Profile] Copying email to clipboard:", email);
    try {
      await navigator.clipboard.writeText(email);
      return true;
    } catch (err: unknown) {
      applicationLogger.error(
        "Failed to copy email to clipboard",
        "useProfileDetailController.handleEmailContact",
        err instanceof Error ? err : new Error(String(err)),
      );
      showToast(
        i18n.t("user.directory.toast.emailCopyFailed"),
        i18n.t("user.directory.toast.emailCopyFailedDescription"),
        "error",
      );
      return false;
    }
  };
}

/**
 * Opens WhatsApp with phone + pre-filled message; template shared with backend magic links (PLA-326/PLA-328).
 */
function createWhatsAppHandler(
  contactInfo: ProfileContactInfo | null,
  participantIdPublic: string,
  userName: string | null,
  otherName: string | null,
  eventName: string | null,
): () => void {
  return (): void => {
    if (contactInfo?.phone === null || contactInfo?.phone === undefined) {
      applicationLogger.warn(
        "[Profile] WhatsApp contact action triggered but no phone available",
        "useProfileDetailController.handleWhatsAppContact",
        [{ participantIdPublic }],
      );
      showToast(
        i18n.t("user.directory.toast.noPhoneAvailable"),
        i18n.t("user.directory.toast.noPhoneAvailableDescription"),
        "error",
      );
      return;
    }

    const phone = String(contactInfo.phone).replace(/^\+/, "");
    applicationLogger.debug("[Profile] Opening WhatsApp for phone:", phone);

    // Build pre-filled message using shared template (PLA-326/PLA-328)
    const message = i18n.t("shared.whatsapp.connectedMessage", {
      userName: userName ?? "",
      otherName: otherName ?? "",
      eventName: eventName ?? "",
    });
    const encodedMessage = encodeURIComponent(message);

    window.open(`https://wa.me/${phone}?text=${encodedMessage}`, "_blank");
  };
}

export const ProfileTab = {
  PROFILE: "profile",
  WHY_CONNECT: "why-connect",
} as const;

export type ProfileTabType = (typeof ProfileTab)[keyof typeof ProfileTab];

export interface ProfileDetailControllerState {
  readonly eventIdPublic: string;
  readonly participantIdPublic: string;

  readonly isLoading: boolean;

  readonly error: Error | null;

  readonly isError: boolean;

  readonly profile: DirectoryProfile | undefined;

  readonly isNotFound: boolean;
  /** Active tab (profile or why-connect) */
  readonly activeTab: ProfileTabType;

  readonly isRequestingIntro: boolean;

  readonly isRespondingToRequest: boolean;

  readonly mutationErrorMessage: string | null;

  readonly isRequestIntroModalOpen: boolean;

  readonly isFeatureNotImplementedModalOpen: boolean;

  readonly contactInfo: ProfileContactInfo | null;

  readonly isContactInfoLoading: boolean;

  readonly hasEmail: boolean;

  readonly hasPhone: boolean;

  readonly event: EventPublicView | undefined;

  readonly isEventLoading: boolean;

  readonly themeMode: ThemeModeType | undefined;
}

export interface ProfileDetailControllerActions {
  readonly handleBack: () => void;

  readonly setActiveTab: (tab: ProfileTabType) => void;

  readonly openRequestIntroModal: () => void;

  readonly closeRequestIntroModal: () => void;

  readonly handleSubmitIntroRequest: (message: string) => Promise<void>;

  readonly handleAcceptRequest: () => Promise<void>;

  readonly handleIgnoreRequest: () => Promise<void>;

  readonly handleEmailContact: () => Promise<boolean>;

  readonly handleWhatsAppContact: () => void;

  readonly handleRetry: () => void;

  readonly clearMutationError: () => void;

  readonly closeFeatureNotImplementedModal: () => void;

  readonly handleEditProfile: () => void;
}

/** How long the responding state waits for the refreshed profile before it lets go. */
const PROFILE_SETTLE_TIMEOUT_MS = 1200;

interface BaseCTAProps {
  readonly isLoading: boolean;
}

export interface CTAPropsNone extends BaseCTAProps {
  readonly connectionStatus: "none";
  readonly onRequestIntro: () => void;
}

export interface CTAPropsRequested extends BaseCTAProps {
  readonly connectionStatus: "requested";
}

export interface CTAPropsIgnored extends BaseCTAProps {
  readonly connectionStatus: "ignored";
}

export interface CTAPropsInbound extends BaseCTAProps {
  readonly connectionStatus: "inbound";
  readonly requestDate: Date | null;
  readonly onAccept: () => Promise<void>;
  readonly onIgnore: () => Promise<void>;
}

export interface CTAPropsConnected extends BaseCTAProps {
  readonly connectionStatus: "connected";
  readonly connectedDate: Date | null;
  readonly onEmail: () => Promise<boolean>;
  readonly onWhatsApp: () => void;

  readonly hasEmail: boolean;

  readonly hasPhone: boolean;

  readonly isContactInfoPending: boolean;
}

export type ProfileDetailCTAProps =
  | CTAPropsNone
  | CTAPropsRequested
  | CTAPropsIgnored
  | CTAPropsInbound
  | CTAPropsConnected;

export interface ProfileDetailControllerResult {
  readonly state: ProfileDetailControllerState;
  readonly actions: ProfileDetailControllerActions;
  readonly ctaProps: ProfileDetailCTAProps | null;
  /** Suggestions for the viewed participant (Why Connect tab) */
  readonly suggestions: readonly Suggestion[];

  readonly isSuggestionsLoading: boolean;
  /** Whether to show Why Connect tab (false for own profile) */
  readonly showWhyConnect: boolean;

  readonly isOwnProfile: boolean;
}

// eslint-disable-next-line complexity -- Controller hooks legitimately orchestrate many pieces of state, queries, mutations, and callbacks
export function useProfileDetailController(): ProfileDetailControllerResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const { participantIdPublic = "" } = useParams<{
    participantIdPublic: string;
  }>();

  const eventContext = useRequiredEventRouteContext();
  const eventIdPublic = eventContext.eventIdPublic;
  const eventQuery = {
    data: eventContext.event,
    isLoading: eventContext.isLoading,
  };
  const nav = eventContext.nav;
  const areQueriesEnabled = EventIdPublic.unwrap(eventIdPublic).length > 0;

  const userActions = useUserActions();
  const eventActions = userActions.useForEvent({
    eventIdPublicOrSlug: EventIdPublicOrSlug.unsafeCreate(
      eventContext.eventIdPublicOrSlug,
    ),
  });
  const participantActions = eventActions.useForParticipant({ self: true });
  const requestIntro = participantActions.mutations?.requestIntro;

  const currentUser = userActions.user;
  const currentUserName = useMemo((): string | null => {
    if (currentUser?.identity === undefined) {
      return null;
    }
    return UserIdentity.isValid(currentUser.identity)
      ? UserIdentity.getDisplayName(currentUser.identity)
      : null;
  }, [currentUser]);

  const currentUserParticipantIdPublic =
    participantActions.participant?.idPublic ?? null;

  // Read initial tab from URL search params (e.g., ?tab=why-connect)

  const initialTab = useMemo((): ProfileTabType => {
    const tabParam = searchParams.get("tab");
    if (tabParam === ProfileTab.WHY_CONNECT) {
      return ProfileTab.WHY_CONNECT;
    }
    return ProfileTab.PROFILE;
  }, [searchParams]);

  const [activeTab, setActiveTabState] = useState<ProfileTabType>(initialTab);

  const setActiveTab = useCallback(
    (tab: ProfileTabType): void => {
      setActiveTabState(tab);
      setSearchParams(
        (prev) => {
          const updated = new URLSearchParams(prev);
          updated.set("tab", tab);
          return updated;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const profileQuery = eventActions.useGetProfileDetail({
    eventIdPublic: eventIdPublic,
    participantIdPublic:
      EventParticipantIdPublic.unsafeCreate(participantIdPublic),
    enabled: areQueriesEnabled && participantIdPublic.length > 0,
  });

  const { getFeatureConfig, isFeatureEnabled } = useFeatureConfigs({
    context: { eventIdPublic: eventIdPublic },
    enabled: areQueriesEnabled,
  });

  const aiRecommendationsConfig = getFeatureConfig(
    FeatureKey.AI_RECOMMENDATIONS,
  );
  const isAiRecommendationsActive =
    isFeatureEnabled(FeatureKey.AI_RECOMMENDATIONS) === true &&
    aiRecommendationsConfig?.config.active === true;
  const isWhyConnectEnabled =
    isAiRecommendationsActive &&
    aiRecommendationsConfig.config.whyConnect === true;

  const profile = profileQuery.data;
  const isLoading = profileQuery.isLoading;
  const error = profileQuery.error ?? null;
  const isError = error !== null;

  const isNotFound =
    profileQuery.isFetched && !isLoading && !isError && profile === undefined;

  const isConnected =
    profile?.connectionStatus === DirectoryConnectionStatus.CONNECTED;

  // When connected, the granting intro id is `pendingRequestIdPublic` —
  // the resolver guarantees it points at the accepted intro in that state.
  const grantingRequestIdPublic = profile?.pendingRequestIdPublic ?? null;
  const connectionRequestActions = eventActions.useForConnectionRequest(
    grantingRequestIdPublic ?? ConnectionRequestIdPublic.unsafeCreate(""),
  );
  const respond = connectionRequestActions.respond;
  const markSeen = connectionRequestActions.markSeen;
  const isContactInfoQueryEnabled =
    areQueriesEnabled &&
    participantIdPublic.length > 0 &&
    isConnected &&
    grantingRequestIdPublic !== null;
  const contactInfoQuery = connectionRequestActions.useGetContactInfo({
    enabled: isContactInfoQueryEnabled,
  });

  // Note: contactInfoQuery.data IS the ProfileContactInfo directly
  const contactInfo = contactInfoQuery.data ?? null;
  // The panel may not name a channel before the answer is in. `isLoading` alone
  // is not that question: a disabled query is not loading either, and the query
  // stays disabled until the accepted profile lands, so the first connected
  // frame would otherwise render "Copy Email" for contact info nobody has read.
  const isContactInfoPending =
    isContactInfoQueryEnabled &&
    !contactInfoQuery.isSuccess &&
    !contactInfoQuery.isError;
  const isContactInfoLoading = contactInfoQuery.isLoading;
  const hasEmail =
    contactInfo?.email !== null && contactInfo?.email !== undefined;
  const hasPhone =
    contactInfo?.phone !== null && contactInfo?.phone !== undefined;

  const isRequestingIntro = requestIntro?.isPending ?? false;

  // Held past the mutation until the refetched profile carries the new status.
  // The mutation settling is not the moment the answer is on screen: dropping
  // the flag there repainted the idle Accept/Ignore panel for a frame before
  // the connected one arrived, which read as a flicker under the toast.
  const [isSettlingResponse, setIsSettlingResponse] = useState(false);

  const isRespondingToRequest =
    markSeen.isPending || respond.isPending || isSettlingResponse;
  const [mutationErrorMessage, setMutationErrorMessage] = useState<
    string | null
  >(null);

  const [isRequestIntroModalOpen, setIsRequestIntroModalOpen] = useState(false);
  const [
    isFeatureNotImplementedModalOpen,
    setIsFeatureNotImplementedModalOpen,
  ] = useState(false);

  const suggestionsQuery = participantActions.useGetSuggestions({
    eventIdPublic: eventIdPublic,
    participantIdPublic:
      currentUserParticipantIdPublic ??
      EventParticipantIdPublic.unsafeCreate(""),
    enabled:
      areQueriesEnabled &&
      currentUserParticipantIdPublic !== null &&
      isWhyConnectEnabled,
  });

  const suggestionsForParticipant = useMemo((): readonly Suggestion[] => {
    if (suggestionsQuery.data === undefined) {
      return [];
    }
    return suggestionsQuery.data.items.filter(
      (suggestion) =>
        EventParticipantIdPublic.unwrap(
          suggestion.targetParticipantIdPublic,
        ) === participantIdPublic,
    );
  }, [suggestionsQuery.data, participantIdPublic]);

  const themeMode = useMemo((): ThemeModeType | undefined => {
    const themeConfig = getFeatureConfig(FeatureKey.EVENT_THEME);
    if (themeConfig === undefined || !themeConfig.enabled) {
      return undefined;
    }
    return themeConfig.config.themeMode;
  }, [getFeatureConfig]);

  const isSuggestionsLoading = suggestionsQuery.isLoading;

  // Check if viewing own profile - don't show Why Connect tab for own profile
  const isOwnProfile =
    currentUserParticipantIdPublic !== null &&
    EventParticipantIdPublic.unwrap(currentUserParticipantIdPublic) ===
      participantIdPublic;

  const hasProfileBasedSuggestion = useMemo((): boolean => {
    return suggestionsForParticipant.some(
      (suggestion) =>
        suggestion.suggestionType === SuggestionType.PROFILE_BASED,
    );
  }, [suggestionsForParticipant]);

  const showWhyConnect =
    !isOwnProfile &&
    isWhyConnectEnabled &&
    (isSuggestionsLoading || hasProfileBasedSuggestion);

  // Clamp active tab to PROFILE when Why Connect is unavailable so a URL like
  // ?tab=why-connect doesn't leave the page rendering an empty tab body.
  const effectiveActiveTab: ProfileTabType =
    activeTab === ProfileTab.WHY_CONNECT && !showWhyConnect
      ? ProfileTab.PROFILE
      : activeTab;

  // fall back to directory route for direct/external links (PLA-337)
  const handleBack = useCallback((): void => {
    nav.back(() => {
      nav.toDirectory();
    });
  }, [nav]);

  const openRequestIntroModal = useCallback((): void => {
    setIsRequestIntroModalOpen(true);
  }, [setIsRequestIntroModalOpen]);

  const closeRequestIntroModal = useCallback((): void => {
    setIsRequestIntroModalOpen(false);
  }, [setIsRequestIntroModalOpen]);

  const handleSubmitIntroRequest = useCallback(
    async (message: string): Promise<void> => {
      if (requestIntro === undefined) {
        return;
      }
      setMutationErrorMessage(null);
      try {
        const data = await requestIntro.mutateAsync({
          eventIdPublic: eventIdPublic,
          targetParticipantIdPublic:
            EventParticipantIdPublic.unsafeCreate(participantIdPublic),
          ...(message.trim().length > 0 && { message: message.trim() }),
          origin: "direct_request",
          originData: {
            type: "direct_request",
            requestedFromDirectory: true,
          },
        });
        showToast(
          i18n.t("user.directory.toast.introRequested"),
          i18n.t("user.directory.toast.introRequestedDescription", {
            name: data.toUserName,
          }),
          "success",
        );
        setIsRequestIntroModalOpen(false);
      } catch (error) {
        const err = normalizeError(error);
        const errorCode = extractApiErrorCode(err);
        if (errorCode === "INTRO_RATE_LIMIT_EXCEEDED") {
          const errorData = extractApiErrorData(err);
          const payload = IntroRateLimitPayloadSchema.safeParse(errorData);
          // A zero limit means intros are off for the event, not a quota worth
          // quoting: "up to 0 every 1 minute" invites a retry that never works.
          if (payload.success && payload.data.limit > 0) {
            const { limit, retryAfterMinutes } = payload.data;
            const window = DateUtils.formatDurationMinutes(
              retryAfterMinutes,
              i18n.getLocale(),
            );
            const description = i18n.t(
              "user.directory.toast.rateLimitExceededDescription",
              { limit: String(limit), window },
            );
            showToast(
              i18n.t("user.directory.toast.rateLimitExceeded"),
              description,
              "error",
            );
            setMutationErrorMessage(description);
          } else {
            applicationLogger.warn(
              "INTRO_RATE_LIMIT_EXCEEDED missing numeric limit/retryAfterMinutes in error payload",
              "useProfileDetailController.requestIntroMutation",
              [
                {
                  errorData: JSON.stringify(errorData),
                  eventIdPublic: EventIdPublic.unwrap(eventIdPublic),
                  participantIdPublic,
                },
              ],
            );
            showToast(
              i18n.t("user.directory.toast.requestIntroFailed"),
              i18n.t("user.directory.toast.requestIntroFailedDescription"),
              "error",
            );
            setMutationErrorMessage(
              i18n.t("user.directory.profile.error.sendRequest"),
            );
          }
          setIsRequestIntroModalOpen(false);
        } else {
          showToast(
            i18n.t("user.directory.toast.requestIntroFailed"),
            i18n.t("user.directory.toast.requestIntroFailedDescription"),
            "error",
          );
          setMutationErrorMessage(
            i18n.t("user.directory.profile.error.sendRequest"),
          );
        }
        applicationLogger.error(
          "Failed to request intro",
          "useProfileDetailController.requestIntroMutation",
          err,
          [
            {
              eventIdPublic: EventIdPublic.unwrap(eventIdPublic),
              participantIdPublic,
            },
          ],
        );
      }
    },
    [requestIntro, eventIdPublic, participantIdPublic, setMutationErrorMessage],
  );

  const getRequestIdOrShowError = useCallback(
    (action: RespondToRequestActionType): string | null => {
      const requestIdPublic = profile?.pendingRequestIdPublic;
      if (requestIdPublic === null || requestIdPublic === undefined) {
        const actionLabel = action === "accept" ? "Accept" : "Ignore";
        applicationLogger.warn(
          `Cannot ${action} request - no pending request ID`,
          `useProfileDetailController.handle${actionLabel}Request`,
          [
            {
              eventIdPublic: EventIdPublic.unwrap(eventIdPublic),
              participantIdPublic,
            },
          ],
        );
        showToast(
          i18n.t("user.directory.toast.noRequestId"),
          i18n.t("user.directory.toast.noRequestIdDescription"),
          "error",
        );
        return null;
      }
      return ConnectionRequestIdPublic.unwrap(requestIdPublic);
    },
    [profile, eventIdPublic, participantIdPublic],
  );

  const handleRespondToRequest = useCallback(
    async (action: RespondToRequestActionType): Promise<void> => {
      if (getRequestIdOrShowError(action) === null) {
        return;
      }

      setMutationErrorMessage(null);
      setIsSettlingResponse(true);
      try {
        await markSeen.mutateAsync();

        const data = await respond.mutateAsync({ action });

        const isAccepted = data.typeData.status === IntroRequestStatus.ACCEPTED;

        // Wait for the refreshed profile so the panel changes in one step, but
        // never longer than this: the query retries internally, so an unbounded
        // await sat on "Accepting…" for 14s on a failing network — measured —
        // while the page's own error state was already offering Try again.
        // Past the deadline the panel falls back to its pre-refresh state,
        // which is where it was before this fix anyway.
        const refreshed = await Promise.race([
          profileQuery.refetch(),
          new Promise<null>((resolve) => {
            setTimeout(() => {
              resolve(null);
            }, PROFILE_SETTLE_TIMEOUT_MS);
          }),
        ]);
        if (refreshed?.isError === true) {
          applicationLogger.error(
            "Responded to request but could not refresh the profile",
            "useProfileDetailController.respondMutation",
            normalizeError(refreshed.error),
            [
              {
                eventIdPublic: EventIdPublic.unwrap(eventIdPublic),
                participantIdPublic,
              },
            ],
          );
        }

        // After the refresh, so the toast and the panel it describes land together.
        if (isAccepted) {
          showToast(
            i18n.t("user.directory.toast.connected", {
              name: profile?.entry.displayName ?? "",
            }),
            i18n.t("user.directory.toast.connectedDescription"),
            "success",
          );
        } else {
          showToast(
            i18n.t("user.directory.toast.requestDeclined"),
            i18n.t("user.directory.toast.requestDeclinedDescription"),
            "info",
          );
        }
      } catch (error) {
        const err = normalizeError(error);
        showToast(
          i18n.t("user.directory.toast.respondFailed"),
          i18n.t("user.directory.toast.respondFailedDescription"),
          "error",
        );
        setMutationErrorMessage(
          i18n.t("user.directory.profile.error.respondRequest"),
        );
        applicationLogger.error(
          "Failed to respond to request",
          "useProfileDetailController.respondMutation",
          err,
          [
            {
              eventIdPublic: EventIdPublic.unwrap(eventIdPublic),
              participantIdPublic,
            },
          ],
        );
      } finally {
        setIsSettlingResponse(false);
      }
    },
    [
      respond,
      markSeen,
      eventIdPublic,
      getRequestIdOrShowError,
      setMutationErrorMessage,
      profile,
      profileQuery,
      participantIdPublic,
    ],
  );

  const handleAcceptRequest = useCallback(
    async (): Promise<void> => handleRespondToRequest("accept"),
    [handleRespondToRequest],
  );

  const handleIgnoreRequest = useCallback(
    async (): Promise<void> => handleRespondToRequest("decline"),
    [handleRespondToRequest],
  );

  const handleEmailContact = useMemo(
    () => createEmailHandler(contactInfo, participantIdPublic),
    [contactInfo, participantIdPublic],
  );

  const locale = i18n.getLocale();
  const resolvedEventName = useMemo(
    () =>
      eventQuery.data != null
        ? LocalizedTextUtils.resolve(eventQuery.data.name, locale)
        : null,
    [eventQuery.data, locale],
  );

  const handleWhatsAppContact = useMemo(
    () =>
      createWhatsAppHandler(
        contactInfo,
        participantIdPublic,
        currentUserName,
        profile?.entry.displayName ?? null,
        resolvedEventName,
      ),
    [
      contactInfo,
      participantIdPublic,
      currentUserName,
      profile?.entry.displayName,
      resolvedEventName,
    ],
  );

  const handleRetry = useCallback((): void => {
    void profileQuery.refetch();
  }, [profileQuery]);

  const clearMutationError = useCallback((): void => {
    setMutationErrorMessage(null);
  }, [setMutationErrorMessage]);

  const closeFeatureNotImplementedModal = useCallback((): void => {
    setIsFeatureNotImplementedModalOpen(false);
  }, [setIsFeatureNotImplementedModalOpen]);

  const handleEditProfile = useCallback((): void => {
    nav.toProfileEdit();
  }, [nav]);

  const state: ProfileDetailControllerState = {
    eventIdPublic: EventIdPublic.unwrap(eventIdPublic),
    participantIdPublic,
    isLoading,
    error,
    isError,
    profile,
    isNotFound,
    activeTab: effectiveActiveTab,
    isRequestingIntro,
    isRespondingToRequest,
    mutationErrorMessage,
    isRequestIntroModalOpen,
    isFeatureNotImplementedModalOpen,
    contactInfo,
    isContactInfoLoading,
    hasEmail,
    hasPhone,
    event: eventQuery.data,
    isEventLoading: eventQuery.isLoading,
    themeMode,
  };

  const actions: ProfileDetailControllerActions = {
    handleBack,
    setActiveTab,
    openRequestIntroModal,
    closeRequestIntroModal,
    handleSubmitIntroRequest,
    handleAcceptRequest,
    handleIgnoreRequest,
    handleEmailContact,
    handleWhatsAppContact,
    handleRetry,
    clearMutationError,
    closeFeatureNotImplementedModal,
    handleEditProfile,
  };

  const ctaProps: ProfileDetailCTAProps | null = useMemo(() => {
    if (profile === undefined) return null;

    if (
      currentUserParticipantIdPublic !== null &&
      EventParticipantIdPublic.unwrap(currentUserParticipantIdPublic) ===
        participantIdPublic
    )
      return null;

    const baseProps = { isLoading: isRequestingIntro || isRespondingToRequest };

    switch (profile.connectionStatus) {
      case DirectoryConnectionStatus.NONE:
        return {
          ...baseProps,
          connectionStatus: "none" as const,
          onRequestIntro: openRequestIntroModal,
        };

      case DirectoryConnectionStatus.REQUESTED:
        return {
          ...baseProps,
          connectionStatus: "requested" as const,
        };

      case DirectoryConnectionStatus.IGNORED:
        return {
          ...baseProps,
          connectionStatus: "ignored" as const,
        };

      case DirectoryConnectionStatus.INBOUND:
        return {
          ...baseProps,
          connectionStatus: "inbound" as const,
          requestDate: profile.requestCreatedAt ?? null,
          onAccept: handleAcceptRequest,
          onIgnore: handleIgnoreRequest,
        };

      case DirectoryConnectionStatus.CONNECTED:
        return {
          ...baseProps,
          connectionStatus: "connected" as const,
          connectedDate: profile.connectedAt ?? null,
          onEmail: handleEmailContact,
          onWhatsApp: handleWhatsAppContact,
          hasEmail,
          hasPhone,
          isContactInfoPending,
        };

      default:
        return null;
    }
  }, [
    profile,
    currentUserParticipantIdPublic,
    participantIdPublic,
    isRequestingIntro,
    isRespondingToRequest,
    openRequestIntroModal,
    handleAcceptRequest,
    handleIgnoreRequest,
    handleEmailContact,
    handleWhatsAppContact,
    hasEmail,
    hasPhone,
    isContactInfoPending,
  ]);

  return {
    state,
    actions,
    ctaProps,
    suggestions: suggestionsForParticipant,
    isSuggestionsLoading,
    showWhyConnect,
    isOwnProfile,
  };
}
